import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { AgGridAngular } from 'ag-grid-angular';
import {
  ColDef,
  FirstDataRenderedEvent,
  GridSizeChangedEvent,
} from 'ag-grid-community';
import {
  HlmBtnDirective,
  HlmIconComponent,
  HlmSpinnerComponent,
  ToastService,
} from '../../../ui';
import { PermissionService } from '../../../core/auth/permission.service';
import { BrandDomainService } from './brand-domain.service';
import { BrandDomain } from './domain.model';
import {
  BrandDomainActionCellComponent,
  BrandDomainGridContext,
} from './brand-domain-action-cell.component';
import {
  BrandDomainAddDialogComponent,
  BrandDomainAddDialogData,
} from './brand-domain-add-dialog.component';
import { ConfirmDialogComponent } from '../../../shared/confirm-dialog/confirm-dialog.component';

/**
 * Embeddable brand-domains management panel — the per-brand domains grid
 * (domain + mailDomain toggle + Remove), an "Add domain" button, and the
 * `brandManager.write` gating, with NO page hero / breadcrumb / read-only
 * banner. Reused by both {@link BrandDomainsPageComponent} (standalone route
 * `/admin/brands/:brandId/domains`) and the brand detail/edit page.
 */
@Component({
  selector: 'cmt-brand-domains-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AgGridAngular,
    HlmBtnDirective,
    HlmIconComponent,
    HlmSpinnerComponent,
  ],
  templateUrl: './brand-domains-panel.component.html',
  styleUrl: './brand-domains-panel.component.scss',
})
export class BrandDomainsPanelComponent {
  /** Owning brand — drives the load and all mutations. */
  readonly brandId = input.required<number>();
  /** Human-readable name for dialog copy; falls back to `Brand #<id>`. */
  readonly brandName = input<string>('');

  private readonly dialog = inject(MatDialog);
  private readonly toastService = inject(ToastService);
  private readonly brandDomainService = inject(BrandDomainService);
  private readonly permissionService = inject(PermissionService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly canWrite = computed(() => this.permissionService.canWrite('brandManager'));

  protected readonly domains = signal<BrandDomain[]>([]);
  protected readonly loading = signal(true);
  /** Guards against double-click races on a mail toggle while an update is in flight. */
  private readonly toggling = signal(false);

  /** Dialog copy needs a non-empty label even when no name was supplied. */
  private readonly displayName = computed(() => this.brandName() || `Brand #${this.brandId()}`);

  protected readonly columnDefs: ColDef<BrandDomain>[] = [
    {
      field: 'domain',
      headerName: 'Domain',
      flex: 1,
      minWidth: 200,
      cellClass: 'cmt-mono',
    },
    {
      colId: 'brand-domain-mail',
      headerName: 'Mail',
      minWidth: 90,
      maxWidth: 110,
      sortable: false,
      filter: false,
      cellRenderer: BrandDomainActionCellComponent,
    },
    {
      colId: 'brand-domain-actions',
      headerName: 'Actions',
      minWidth: 140,
      maxWidth: 160,
      sortable: false,
      filter: false,
      cellRenderer: BrandDomainActionCellComponent,
    },
  ];

  protected readonly defaultColDef: ColDef = {
    sortable: true,
    resizable: true,
    filter: 'agTextColumnFilter',
    filterParams: {
      maxNumConditions: 1,
      filterOptions: ['contains'],
      buttons: ['clear'],
    },
  };

  protected readonly gridContext: BrandDomainGridContext = {
    canWrite: () => this.canWrite(),
    toggling: () => this.toggling(),
    toggleMail: (row) => this.toggleMail(row),
    confirmDelete: (row) => this.confirmDelete(row),
  };

  /** Stable row identity (domain is the natural key) — avoids full re-render on reload. */
  protected readonly getRowId = (params: { data: BrandDomain }) => params.data.domain;

  constructor() {
    // Reload whenever the bound brandId changes. effect (not subscribe) so the
    // panel reacts to a parent swapping the input — Pitfall #13.
    effect(() => {
      const id = this.brandId();
      if (id == null || Number.isNaN(id)) return;
      this.reload();
    });
  }

  protected reload(): void {
    const id = this.brandId();
    if (id == null || Number.isNaN(id)) return;
    this.loading.set(true);
    this.brandDomainService
      .listBrandDomains(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: list => {
        this.domains.set(list);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.toastService.open('Failed to load brand domains', 'OK', { duration: 5000 });
      },
    });
  }

  protected onFirstDataRendered(event: FirstDataRenderedEvent): void {
    event.api.sizeColumnsToFit();
  }

  protected onGridSizeChanged(event: GridSizeChangedEvent): void {
    event.api.sizeColumnsToFit();
  }

  protected toggleMail(row: BrandDomain): void {
    if (!this.canWrite()) return;
    const id = this.brandId();
    this.toggling.set(true);
    this.brandDomainService
      .updateBrandDomain(id, row.domain, { mailDomain: !row.mailDomain })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.open(
            `${row.domain} mail-domain flag ${!row.mailDomain ? 'enabled' : 'disabled'}`,
            'OK',
            { duration: 3000 },
          );
          this.toggling.set(false);
          this.reload();
        },
        error: () => {
          this.toggling.set(false);
          this.toastService.open('Update failed — please retry.', 'OK', { duration: 4000 });
        },
      });
  }

  protected openAdd(): void {
    if (!this.canWrite()) return;
    const id = this.brandId();
    this.dialog
      .open(BrandDomainAddDialogComponent, {
        data: {
          brandId: id,
          brandName: this.displayName(),
        } satisfies BrandDomainAddDialogData,
        width: '560px',
        maxWidth: '92vw',
        autoFocus: false,
        panelClass: 'cmt-dialog-panel',
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result) this.reload();
      });
  }

  protected confirmDelete(row: BrandDomain): void {
    if (!this.canWrite()) return;
    const id = this.brandId();
    const brandName = this.displayName();
    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: 'Remove domain?',
          message: `${row.domain} will be removed from ${brandName}. Account-recovery checks for this brand will no longer treat it as belonging to the brand.`,
          confirmLabel: 'Remove',
          cancelLabel: 'Cancel',
          confirmColor: 'warn',
          icon: 'warning',
        },
        width: '460px',
        maxWidth: '92vw',
        autoFocus: false,
        panelClass: 'cmt-dialog-panel',
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmed => {
        if (confirmed) this.deleteDomain(id, row);
      });
  }

  private deleteDomain(brandId: number, row: BrandDomain): void {
    this.brandDomainService
      .deleteBrandDomain(brandId, row.domain)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: () => {
        this.toastService.open('Domain removed', 'OK', { duration: 3000 });
        this.reload();
      },
      error: () => {
        this.toastService.open('Delete failed — please retry.', 'OK', { duration: 4000 });
      },
    });
  }
}
